<template>
  <div class="min-h-screen bg-gray-100">
    <div class="mx-auto p-6">
      <div
        class="flex justify-between items-end mb-6 mdmax:flex-col mdmax:items-stretch gap-4"
      >
        <div class="flex-1 mdmax:w-full">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label
                class="text-base text-neutral-700 font-medium mb-1 inline-block"
              >
                Customer
              </label>
              <CustomSelect
                :options="customers"
                :default="selectedCustomer"
                optionLabel="customer_name"
                placeholder="Pilih customer"
                @input="(val) => (selectedCustomer = val)"
                @change="onFilterChange"
              />
            </div>
            <div>
              <label
                class="text-base text-neutral-700 font-medium mb-1 inline-block"
              >
                Destination
              </label>
              <TextField
                type="text"
                placeholder="Cari destination..."
                v-model="destination"
                @enter="onFilterChange"
              />
            </div>
            <div class="flex items-center gap-3 pt-7">
              <input
                id="eligible-only"
                type="checkbox"
                class="w-4 h-4"
                v-model="eligibleOnly"
                @change="onFilterChange"
              />
              <label for="eligible-only" class="text-sm text-neutral-700">
                Eligible only
              </label>
            </div>
          </div>
        </div>

        <div class="flex mdmax:w-full justify-end gap-2">
          <button
            @click="openCreateResiModal"
            class="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium"
          >
            + Tambah Resi
          </button>
        </div>
      </div>

      <TableComponent :columns="columns" :list="tableData">
        <template #table-row="{ entry, column }">
          <span v-if="column.field === 'action'" class="flex gap-2">
            <button
              @click="$router.push(`/invoices/resi/${entry.id || entry.resi_id}`)"
              class="px-3 py-1 rounded bg-neutral-200 text-neutral-700 hover:bg-neutral-300 text-sm"
            >
              Detail
            </button>
          </span>

          <span v-else-if="column.field === 'RowNumber'" class="font-medium">
            {{ entry.RowNumber }}
          </span>

          <span v-else-if="column.field === 'eligible'">
            <span
              class="px-2 py-1 rounded text-xs font-medium"
              :class="
                entry.eligible
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700'
              "
            >
              {{ entry.eligible ? "Eligible" : "Not eligible" }}
            </span>
          </span>

          <span v-else-if="column.field === 'unit_price'">
            {{ formatCurrency(entry.unit_price) }}
          </span>
          <span v-else-if="column.field === 'total_price'">
            {{ formatCurrency(entry.total_price) }}
          </span>

          <span v-else>{{ entry[column.field] || "-" }}</span>
        </template>
      </TableComponent>

      <div
        v-if="tableData.length > 0"
        class="mt-6 mb-6 flex justify-between items-center mdmax:flex-col mdmax:items-start gap-4"
      >
        <div v-if="!isMobile" class="text-sm font-medium text-neutral-500">
          Menampilkan {{ start }} - {{ end }} dari {{ totalRecords }}
        </div>
        <Pagination
          :currentPage="page"
          :totalCount="totalRecords"
          :pageSize="limit"
          @onPageChange="changePage"
        />
      </div>

      <Modal
        :modalVisible="createModalVisible"
        @close="closeCreateResiModal"
        width="720px"
      >
        <div slot="modal-content" class="text-left">
          <h3 class="text-lg font-bold text-neutral-800 mb-4">
            Tambah Resi
          </h3>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="md:col-span-2">
              <TextField
                type="date"
                label="Date"
                required
                v-model="createForm.date"
              />
            </div>

            <div class="md:col-span-2">
              <label
                class="text-sm text-neutral-700 font-medium mb-1 inline-block"
              >
                Customer
              </label>
              <CustomSelect
                :options="customers"
                :default="createFormCustomer"
                optionLabel="customer_name"
                placeholder="Pilih customer"
                @input="onCreateCustomerSelect"
              />
            </div>

            <div>
              <label
                class="text-sm text-neutral-700 font-medium mb-1 inline-block"
              >
                <span class="text-system-error">*</span>Destination
              </label>
              <CustomSelect
                :options="destinationOptions"
                :default="createForm.destination"
                placeholder="Pilih destination"
                @input="(val) => (createForm.destination = val)"
              />
              <p
                v-if="destinationOptionsLoading"
                class="text-xs text-neutral-500 mt-1"
              >
                Memuat destination...
              </p>
              <p
                v-else-if="createFormCustomer && destinationOptions.length === 0"
                class="text-xs text-neutral-500 mt-1"
              >
                Belum ada konfigurasi harga untuk customer ini. Tambahkan di
                Customers → Prices.
              </p>
            </div>

            <div>
              <TextField
                label="Resi Number"
                required
                v-model="createForm.resi_number"
                placeholder="Nomor resi"
              />
            </div>

            <div class="md:col-span-2">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label class="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    class="w-4 h-4"
                    v-model="createForm.has_weight_detail"
                  />
                  has_weight_detail
                </label>
                <label class="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    class="w-4 h-4"
                    v-model="createForm.has_white_receipt"
                  />
                  has_white_receipt
                </label>
                <label class="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    class="w-4 h-4"
                    v-model="createForm.has_blue_receipt"
                  />
                  has_blue_receipt
                </label>
                <label class="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    class="w-4 h-4"
                    v-model="createForm.has_delivery_order"
                  />
                  has_delivery_order
                </label>
                <label class="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    class="w-4 h-4"
                    v-model="createForm.is_wooden_packing"
                  />
                  is_wooden_packing
                </label>
              </div>
            </div>
          </div>

          <div class="flex justify-end gap-3 mt-6">
            <button
              @click="closeCreateResiModal"
              class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
            >
              Batal
            </button>
            <button
              @click="submitCreateResi"
              :disabled="
                !createForm.date ||
                !createFormCustomer ||
                !createForm.destination ||
                !createForm.resi_number
              "
              class="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Simpan
            </button>
          </div>
        </div>
      </Modal>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import { mapActions, mapGetters } from "vuex"
import TableComponent from "@/components/Table/Table"
import Pagination from "@/components/Pagination/Pagination"
import TextField from "@/components/Forms/TextField"
import CustomSelect from "@/components/Forms/CustomSelect"
import Modal from "@/components/Modal/Modal"

export default {
  name: "InvoicesResi",
  components: {
    TableComponent,
    Pagination,
    TextField,
    CustomSelect,
    Modal
  },
  data() {
    return {
      customers: [],
      selectedCustomer: null,
      destination: "",
      eligibleOnly: false,

      page: 1,
      limit: 10,
      meta: {},
      list: [],
      columns: [
        { label: "No", field: "RowNumber" },
        { label: "Resi Number", field: "resi_number" },
        { label: "Customer", field: "customer_name" },
        { label: "Destination", field: "destination" },
        { label: "Unit Price", field: "unit_price" },
        { label: "Total Price", field: "total_price" },
        { label: "Eligible", field: "eligible" },
        { label: "Action", field: "action" }
      ],

      createModalVisible: false,
      createFormCustomer: null,
      createForm: {
        date: (() => {
          const d = new Date()
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        })(),
        destination: "",
        resi_number: "",
        has_weight_detail: false,
        has_white_receipt: false,
        has_blue_receipt: false,
        has_delivery_order: false,
        is_wooden_packing: false
      },
      destinationOptions: [],
      destinationOptionsLoading: false
    }
  },
  computed: {
    ...mapGetters("common", ["isMobile"]),
    totalRecords() {
      return (
        this.meta?.total_item ||
        this.meta?.total ||
        this.meta?.totalItems ||
        0
      )
    },
    tableData() {
      return (this.list || []).map((item, index) => ({
        ...item,
        RowNumber: (this.page - 1) * this.limit + index + 1
      }))
    },
    start() {
      return this.totalRecords ? (this.page - 1) * this.limit + 1 : 0
    },
    end() {
      return Math.min(this.page * this.limit, this.totalRecords)
    }
  },
  methods: {
    ...mapActions("common", ["showLoading", "hideLoading", "setToast"]),
    toastSuccess(message, secondaryText = "") {
      this.setToast({
        options: {
          type: "success",
          duration: 2500,
          primaryText: message,
          secondaryText
        }
      })
    },
    toastError(message, secondaryText = "") {
      this.setToast({
        options: {
          type: "danger",
          duration: 3500,
          primaryText: message,
          secondaryText
        }
      })
    },
    getErrorMessage(err, fallback = "Terjadi kesalahan") {
      return (
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        fallback
      )
    },
    unwrapList(envelope) {
      const data = envelope?.data?.data
      const meta = envelope?.data?.meta || envelope?.data?.data?.meta || {}
      const items = Array.isArray(data) ? data : data?.items || data?.data || []
      return { items, meta }
    },
    toDecimalString(value, fallback = "0") {
      const raw = String(value ?? "").trim()
      if (!raw) return fallback
      const normalized = raw.replace(/,/g, ".").replace(/[^0-9.-]/g, "")
      return normalized || fallback
    },
    formatCurrency(value) {
      const numberValue = Number(this.toDecimalString(value, "0"))
      if (Number.isNaN(numberValue)) return "Rp 0"
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0
      }).format(numberValue)
    },

    async fetchDestinationOptions(customerId) {
      if (!customerId) {
        this.destinationOptions = []
        return
      }

      this.destinationOptionsLoading = true
      try {
        const res = await api.invoiceGenerator.customerPriceDestinations({
          customer_id: customerId
        })
        const raw = res?.data?.data
        const rows = Array.isArray(raw) ? raw : raw?.items || raw?.data || []
        const destinations = Array.from(
          new Set(
            (rows || [])
              .map((r) => r?.destination)
              .filter((destination) => !!destination)
          )
        ).sort((a, b) => String(a).localeCompare(String(b)))

        this.destinationOptions = destinations
        if (destinations.length === 1) {
          this.createForm.destination = destinations[0]
        }
      } catch (err) {
        this.destinationOptions = []
        this.toastError(
          this.getErrorMessage(err, "Gagal memuat destination options")
        )
      } finally {
        this.destinationOptionsLoading = false
      }
    },
    onCreateCustomerSelect(val) {
      this.createFormCustomer = val
      this.createForm.destination = ""
      this.destinationOptions = []

      const customerId = val?.id || val?.customer_id
      this.fetchDestinationOptions(customerId)
    },

    async fetchCustomers() {
      try {
        const res = await api.invoiceGenerator.customers({ page: 1, per_page: 500 })
        const { items } = this.unwrapList(res)
        this.customers = items
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal memuat customer"))
      }
    },

    async fetchResi() {
      this.showLoading()
      try {
        const customerId =
          this.selectedCustomer?.id || this.selectedCustomer?.customer_id
        const res = await api.invoiceGenerator.resi({
          page: this.page,
          per_page: this.limit,
          customer_id: customerId || undefined,
          eligible_only: this.eligibleOnly ? true : undefined,
          destination: this.destination || undefined
        })
        const { items, meta } = this.unwrapList(res)
        this.list = items
        this.meta = meta || {}
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal memuat resi"))
      } finally {
        this.hideLoading()
      }
    },

    onFilterChange() {
      this.page = 1
      this.fetchResi()
    },
    changePage(val) {
      this.page = val
      this.fetchResi()
    },

    openCreateResiModal() {
      const d = new Date()
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

      this.createFormCustomer = this.selectedCustomer
      this.createForm = {
        date: today,
        destination: "",
        resi_number: "",
        has_weight_detail: false,
        has_white_receipt: false,
        has_blue_receipt: false,
        has_delivery_order: false,
        is_wooden_packing: false
      }
      this.destinationOptions = []
      this.destinationOptionsLoading = false
      this.createModalVisible = true
    },
    closeCreateResiModal() {
      this.createModalVisible = false
    },
    async submitCreateResi() {
      const customerId =
        this.createFormCustomer?.id || this.createFormCustomer?.customer_id
      if (!customerId) return

      const payloads = {
        date: this.createForm.date,
        invoice_customer_id: customerId,
        destination: this.createForm.destination,
        resi_number: this.createForm.resi_number,
        has_weight_detail: !!this.createForm.has_weight_detail,
        has_white_receipt: !!this.createForm.has_white_receipt,
        has_blue_receipt: !!this.createForm.has_blue_receipt,
        has_delivery_order: !!this.createForm.has_delivery_order,
        is_wooden_packing: !!this.createForm.is_wooden_packing
      }

      this.showLoading()
      try {
        const res = await api.invoiceGenerator.createResi(payloads)
        this.toastSuccess(res?.data?.message || "Resi berhasil dibuat")
        this.closeCreateResiModal()
        this.fetchResi()
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal membuat resi"))
      } finally {
        this.hideLoading()
      }
    }
  },
  async mounted() {
    await this.fetchCustomers()
    this.fetchResi()
  }
}
</script>
